import type { SeasonRoundItem } from "../leagueFormSchema";
import { DateTime } from "luxon";
import type {
  Clubhouse_PostLeagueV2Round,
  Clubhouse_PatchLeagueV2Round,
  Clubhouse_PatchLeagueV2RoundSettings,
} from "../../types/leagues";
import { toFacilityDateOnly } from "../helpers";

export type RoundMappingOptions = {
  timezone?: string;
  seasonStartUtc?: string;
  seasonEndUtc?: string;
  original?: {
    playWindowStartUtc?: string;
    playWindowEndUtc?: string;
  };
  /**
   * Edit Round and Add Round cannot change a play window. Pass false so a
   * settings/course save leaves the stored window alone. Weekday reschedules
   * still send one (the default).
   */
  includePlayWindow?: boolean;
  /**
   * League create/replace identifies Season Flex by rounds that omit both
   * play windows. Leave this false for the single-round POST, which still
   * requires a window and uses the season-bounds fallback below.
   */
  omitPlayWindow?: boolean;
};

/**
 * Single source of truth for the round create/edit *wire* shape.
 *
 * The form works in one convenient internal model (`SeasonRoundItem`, whose
 * `settings` is `Clubhouse_RoundSetting`). The API, however, wants two subtly
 * different envelopes:
 *   - CREATE (`Clubhouse_PostLeagueV2Round`): settings under `settings`, holes
 *     at top level as `selectedHolesJson`.
 *   - PATCH  (`Clubhouse_PatchLeagueV2Round`): settings under `roundSettings`,
 *     holes at top level as `selectedHoles`.
 * In both, holes are a JSON number-array *string* (e.g. "[1, 2, 3]") and never
 * live inside the settings object — `settings.selectedHoles` is a frontend-only
 * convenience field.
 *
 * Keeping both envelopes here means integration-day contract tweaks (wrapper
 * key, holes key, enum representation) are a one-line change, not a hunt through
 * component handlers.
 */

export const FRONT_9_HOLES = [1, 2, 3, 4, 5, 6, 7, 8, 9];
export const ALL_18_HOLES = Array.from({ length: 18 }, (_, i) => i + 1);

/** Serialize the form's holes preset to the stored `selected_holes_json` form. */
export function serializeSelectedHoles(
  holes: number[] | undefined,
): string | undefined {
  return holes && holes.length > 0 ? JSON.stringify(holes) : undefined;
}

/** Create payloads always write a hole list — missing means Front 9, never omit. */
export function serializeSelectedHolesForCreate(
  holes: number[] | undefined,
): string {
  return serializeSelectedHoles(holes) ?? JSON.stringify(FRONT_9_HOLES);
}

/**
 * Strip the frontend-only `selectedHoles` convenience field out of settings so
 * it isn't sent inside the settings object (holes go top-level). Returns
 * undefined when there are no settings to send.
 */
function toWireSettings(
  settings: SeasonRoundItem["settings"],
): Clubhouse_PatchLeagueV2RoundSettings | undefined {
  if (!settings) return undefined;
  const { selectedHoles: _selectedHoles, ...rest } = settings;
  // A settings object holding nothing but holes — or nothing but keys whose
  // values are absent — must not be sent as `{}`. An empty object is the
  // absence of settings, and the wire should say so rather than leave the
  // backend to decide whether it means "no change" or "clear them all".
  //
  // Counting keys is not enough: a key with an `undefined` value looks present
  // here and then vanishes in serialization, producing exactly the `{}` this
  // guard exists to prevent. Filter first, then decide.
  //
  // Only `undefined` is dropped — 0 and false are real settings.
  const wire = Object.fromEntries(
    Object.entries(rest).filter(([, value]) => value !== undefined),
  ) as Clubhouse_PatchLeagueV2RoundSettings;
  return Object.keys(wire).length > 0 ? wire : undefined;
}

/**
 * Convert an API UTC instant back to the local facility calendar date used by
 * the date-only schedule controls. Date-only values are already local dates
 * and must pass through unchanged.
 */
export function toLocalDateOnly(
  value: string | undefined,
  timezone?: string,
  boundary: "start" | "end" = "start",
): string | undefined {
  return toFacilityDateOnly(value, boundary, timezone);
}

/**
 * Round and season boundaries are date-only values in the UI. The API contract
 * requires UTC DateTime values, so serialize them without consulting the
 * operator's browser timezone. Existing exact boundaries are preserved when
 * they are unchanged.
 *
 * Exported so callers that only hold a date-only season boundary (e.g. a
 * create-round call with no fetched league detail to source a UTC instant
 * from) can convert it themselves before passing it as `seasonStartUtc`/
 * `seasonEndUtc` — those options are written to the wire as-is, unconverted.
 */
export function toDateOnly(
  value: string | undefined,
  boundary: "start" | "end",
  options?: RoundMappingOptions,
): string | undefined {
  if (!value) return value;
  if (value.includes("T")) return value;

  const seasonBoundary =
    boundary === "start" ? options?.seasonStartUtc : options?.seasonEndUtc;
  const originalBoundary =
    boundary === "start"
      ? options?.original?.playWindowStartUtc
      : options?.original?.playWindowEndUtc;
  const exactBoundary = [seasonBoundary, originalBoundary].find(
    (candidate) =>
      candidate &&
      (toLocalDateOnly(candidate, options?.timezone, boundary) === value ||
        // Raw UTC YYYY-MM-DD only when we have no league TZ. With a TZ, that
        // prefix is the UTC calendar day and can match a different local date.
        (!options?.timezone && candidate.slice(0, 10) === value)),
  );
  if (exactBoundary) {
    return exactBoundary;
  }

  const localDate = DateTime.fromISO(value, {
    zone: options?.timezone ?? "UTC",
  });
  if (!localDate.isValid) return value;
  const boundaryDate =
    boundary === "end"
      ? localDate.endOf("day").set({ millisecond: 0 })
      : localDate.startOf("day");
  return boundaryDate.toUTC().toISO({ suppressMilliseconds: true }) ?? value;
}

export function toCreateRoundBody(
  item: SeasonRoundItem,
  options?: RoundMappingOptions,
): Clubhouse_PostLeagueV2Round {
  return {
    roundNumber: item.roundNumber,
    // name + competitionType intentionally omitted — the backend assigns the
    // name and derives competitionType from the league's scoring type. (Our
    // Clubhouse_LeagueScoring is a string enum like "STROKE"; the round's
    // competitionType is an integer server-side, so we must not send the string.)
    courseId: item.courseId,
    // Fixed-time rounds send their own play window. A single-round POST
    // still rejects a round with no window, so that caller passes season
    // bounds and a Flex round uses them. League create must not: omitted
    // windows are how the API stores Season Flex, and a filled-in window
    // with no schedule mode is stored as Weekly.
    ...(options?.omitPlayWindow
      ? {}
      : item.playWindowStartUtc && item.playWindowEndUtc
        ? {
            playWindowStartUtc: toDateOnly(
              item.playWindowStartUtc,
              "start",
              options,
            ),
            playWindowEndUtc: toDateOnly(item.playWindowEndUtc, "end", options),
          }
        : options?.seasonStartUtc && options?.seasonEndUtc
          ? {
              playWindowStartUtc: options.seasonStartUtc,
              playWindowEndUtc: options.seasonEndUtc,
            }
          : item.dueByUtc
            ? { dueByUtc: item.dueByUtc }
            : {}),
    selectedHolesJson: serializeSelectedHolesForCreate(
      item.settings?.selectedHoles,
    ),
    settings: toWireSettings(item.settings),
  };
}

export function toPatchRoundBody(
  item: SeasonRoundItem,
  options?: RoundMappingOptions,
): Clubhouse_PatchLeagueV2Round {
  // name intentionally omitted — round names are backend-generated and read-only
  // in the editor, and the PATCH contract doesn't accept it.
  const start = toDateOnly(item.playWindowStartUtc, "start", options);
  const end = toDateOnly(item.playWindowEndUtc, "end", options);
  const includePlayWindow = options?.includePlayWindow !== false;

  return {
    // Omitted rather than sent empty. A flex round has no window and reaches
    // here with "", which is not a date and not a clear — it is the absence of
    // a window, and the wire should say nothing rather than say "". Edit and
    // create-from-the-round-dialog also omit a real window: the operator can't
    // change it, and echoing it would rewrite the stored bounds.
    ...(includePlayWindow && start ? { playWindowStartUtc: start } : {}),
    ...(includePlayWindow && end ? { playWindowEndUtc: end } : {}),
    // Only a real Course.id goes on the wire, so a caller that couldn't resolve
    // one passes 0. Omit it: an absent courseId leaves the round's course
    // unchanged (confirmed against the API), which is what an unresolved course
    // should do.
    ...(Number.isInteger(item.courseId) && item.courseId > 0
      ? { courseId: item.courseId }
      : {}),
    selectedHoles: serializeSelectedHoles(item.settings?.selectedHoles),
    roundSettings: toWireSettings(item.settings),
  };
}
